import type { NextRouter } from 'next/router';
import Router from 'next/router';
import type { ParsedUrlQuery } from 'querystring';

import { triggerAccountSortEvent } from 'Services/analytics/events';
import { setAccountFilter } from 'Stores/AccountFiltersStore/AccountFiltersStore.helpers';
import { AccountCategory, AccountDateRange, AccountSlugs, SortBy, SortByBEOptions } from 'Types/account';
import { Order } from 'Types/generic';
import { PurchasesCompleteStatusTypes } from 'Types/purchaseCompleteStatus';
import type { VehicleAccountListItem } from 'Types/vehicle';
import { isTrue } from 'Utilities/helpers';
import { getAccountDateRange } from 'Utilities/helpers/getAccountDateRange';
import type { AccountVehicleListParams } from 'Utilities/hooks/account/useAccountVehicleList';

import { isCompletedPage } from './Purchases/Purchases.helpers';
import { DEFAULT_PAGE_OFFSET, DEFAULT_START_PAGE, DEFAULT_VEHICLES_PAGE_SIZE } from './AccountPage.consts';

export const isAccountCategory = (str: unknown): str is AccountCategory =>
	typeof str === 'string' && Object.values(AccountCategory).includes(str as AccountCategory);

export const isAccountSlug = (str: unknown): str is AccountSlugs =>
	typeof str === 'string' && Object.values(AccountSlugs).includes(str as AccountSlugs);

export const isPurchasesCategory = (category: unknown): boolean => category === AccountCategory.PURCHASES;
export const isBidsCategory = (category: unknown): boolean => category === AccountCategory.BIDS;

export const isAccountDateRange = (val: unknown): val is AccountDateRange =>
	Object.values(AccountDateRange)?.includes(val as AccountDateRange);

export const isOrder = (val: unknown): val is Order => Object.values(Order)?.includes(val as Order);

export const isSortBy = (val: unknown): val is SortBy => Object.values(SortBy)?.includes(val as SortBy);

export const isPurchasesCompleteStatusTypes = (
	val: PurchasesCompleteStatusTypes | unknown,
): val is PurchasesCompleteStatusTypes =>
	Object.values(PurchasesCompleteStatusTypes)?.includes(val as PurchasesCompleteStatusTypes);

export const parseDateRangeType = (query: ParsedUrlQuery, defaultValue: AccountDateRange): AccountDateRange =>
	query?.dateRangeType && isAccountDateRange(query.dateRangeType) ? query.dateRangeType : defaultValue;

export const parseKeyword = (query: ParsedUrlQuery, defaultValue?: string): string | undefined =>
	query?.keyword ? String(query.keyword) : defaultValue;

export const parseSortBy = (query: ParsedUrlQuery): SortBy => {
	const { params, sortBy } = query;

	const [, slug] = params || [];

	const defaultSort = isCompletedPage(slug) ? SortBy.DATE_WON : SortBy.DATE_BID;

	return sortBy && isSortBy(sortBy) ? sortBy : defaultSort;
};

export const parseOrder = (query: ParsedUrlQuery, defaultValue: Order): Order =>
	query.order && isOrder(query.order) ? query.order : defaultValue;

export const parsePage = (query: ParsedUrlQuery, defaultValue: number): number => {
	const hasPages = query?.page && Number(query.page) > 0;
	return hasPages ? Number(query.page) : defaultValue;
};

export const parseHideDelivered = (query: ParsedUrlQuery): boolean => {
	return isTrue(String(query.hideDelivered));
};

export const parsePurchasesCompleteStatusType = (
	query: ParsedUrlQuery,
	defaultValue: PurchasesCompleteStatusTypes,
): string =>
	query?.purchasesCompleteStatusType && isPurchasesCompleteStatusTypes(query.purchasesCompleteStatusType)
		? query.purchasesCompleteStatusType
		: defaultValue;

export const getQueryOffset = (query: ParsedUrlQuery): number =>
	(parsePage(query, DEFAULT_START_PAGE) - DEFAULT_PAGE_OFFSET) * DEFAULT_VEHICLES_PAGE_SIZE;

/**
 * Backend uses different names for sort options.
 * These don't make sense visually. Convert
 * names used in the table columns to
 * options understood by the BE.
 */
export const sortByOptionsMap: { [key in SortBy]: SortByBEOptions } = {
	[SortBy.DATE_BID]: SortByBEOptions.BIDS_CREATED_AT,
	[SortBy.DATE_WON]: SortByBEOptions.ENQUIRY_SOLD_DATE,
	[SortBy.MAX_BID_OFFER]: SortByBEOptions.BID_VALUE,
	[SortBy.PRICE]: SortByBEOptions.BID_VALUE,
	[SortBy.VEHICLES]: SortByBEOptions.MAKE_MODEL,
};

const convertSortByOption = <T extends string | number | symbol, U>(value: T, mapping: { [key in T]: U }): U =>
	mapping[value];

export const parseQueryObject = (query: ParsedUrlQuery): AccountVehicleListParams => {
	const { params } = query;

	const [category, slug] = params || [];

	const { from, to } = getAccountDateRange(parseDateRangeType(query, AccountDateRange.ALL));

	const queryObject = {
		category: String(category),
		from,
		keyword: parseKeyword(query),
		limit: DEFAULT_VEHICLES_PAGE_SIZE,
		offset: getQueryOffset(query),
		...(isCompletedPage(slug) ? { hideDeliveredEnquiries: parseHideDelivered(query) } : {}),
		order: parseOrder(query, Order.Descending),
		purchasesCompleteStatusType: parsePurchasesCompleteStatusType(query, PurchasesCompleteStatusTypes.All),
		slug: String(slug),
		sortBy: convertSortByOption(parseSortBy(query), sortByOptionsMap),
		to,
	};

	return queryObject;
};

export const onSortHandler = (id: string, newOrder: string, router: NextRouter): void => {
	const {
		query: { params },
	} = router;

	const [, slug] = params || [];

	if (isAccountSlug(slug) && isOrder(newOrder)) {
		triggerAccountSortEvent({ id, slug, sortingOrder: newOrder });
	}

	setAccountFilter(router, { order: newOrder as Order, sortBy: id as SortBy });
};

export const structureMockVehicleApiResponse = (
	vehicleData: Array<VehicleAccountListItem>,
): { count: number; results: Array<VehicleAccountListItem> } => ({
	count: vehicleData.length,
	results: vehicleData,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export const setupRouteSpies = (routerSpies: any): void => {
	routerSpies.routerChangeStart = jest.fn();
	Router.events.on('routeChangeStart', routerSpies.routerChangeStart);
};
